import { useEffect, useRef, useState } from 'react';
import { Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { UPPERCASE_LABEL_CLASS_NAME } from '../../FieldServices/constants';
import { getAlertCeilingUtilization } from './constants';

interface AlertCeilingUtilizationProps {
  ceilingPct: number;
  isCustomized: boolean;
  canManage: boolean;
  totalSlots: number;
  isSaving: boolean;
  onCommit: (value: number) => void;
  onReset: () => void;
  mayNotFitCount?: number;
}

const CHART_TEAL = '#13D8AA';

const AlertCeilingUtilization = ({
  ceilingPct,
  isCustomized,
  canManage,
  totalSlots,
  isSaving,
  onCommit,
  onReset,
  mayNotFitCount = 0,
}: AlertCeilingUtilizationProps) => {
  const { t } = useTranslation();
  const [capPct, setCapPct] = useState(ceilingPct);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const committedPct = useRef<number | null>(null);

  useEffect(() => {
    if (!isSaving) {
      setCapPct(ceilingPct);
      committedPct.current = null;
    }
  }, [ceilingPct, isSaving]);

  const isLocked = !canManage || isSaving;
  const { capSlots, roomSlots } = getAlertCeilingUtilization(capPct, totalSlots);

  const commitCap = () => {
    if (isLocked || capPct === ceilingPct || committedPct.current === capPct) return;
    committedPct.current = capPct;
    onCommit(capPct);
  };

  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between gap-2">
        <span className={UPPERCASE_LABEL_CLASS_NAME}>{t('Schedule.Detail.Sidebar.AlertCeiling')}</span>
        <div className="flex items-center gap-2">
          {mayNotFitCount > 0 && (
            <span className="whitespace-nowrap rounded-[5px] border border-warning-200 bg-warning-50 px-[7px] py-0.5 text-[10px] font-bold text-warning-700">
              {t('Schedule.Detail.Sidebar.Utilization.MayNotFitCount', { count: mayNotFitCount })}
            </span>
          )}
          <button
            type="button"
            aria-label={t('Schedule.Detail.Sidebar.Utilization.InfoTitle')}
            aria-expanded={isInfoOpen}
            onClick={() => setIsInfoOpen((open) => !open)}
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded-md border',
              isInfoOpen
                ? 'border-brand-200 bg-brand-50 text-brand-600'
                : 'border-transparent text-gray-400 hover:text-gray-600',
            )}>
            <Info size={12} />
          </button>
        </div>
      </div>

      {isInfoOpen && (
        <div className="mt-2 rounded-[10px] border border-brand-200 bg-brand-50 px-3 py-2.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-900">
              {t('Schedule.Detail.Sidebar.Utilization.InfoTitle')}
            </span>
            <button type="button" onClick={() => setIsInfoOpen(false)} className="text-gray-400 hover:text-gray-600">
              <X size={12} />
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">{t('Schedule.Detail.Sidebar.Utilization.InfoNote')}</p>
          <p className="mt-2 border-t border-brand-100 pt-2 text-[11px] text-gray-400">
            {t('Schedule.Detail.Sidebar.Utilization.InfoContractNote', { typicalSlots: totalSlots })}
          </p>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-gray-500">{t('Schedule.Detail.Sidebar.Utilization.Cap')}</span>
        <span className="text-xs font-bold text-gray-900">
          {t('Schedule.Detail.Sidebar.Utilization.Percent', { value: capPct })}
        </span>
      </div>
      {isCustomized && (
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="whitespace-nowrap rounded-full bg-brand-50 px-2 py-0.5 text-[10.5px] font-medium text-brand-700">
            {t('Schedule.Detail.Sidebar.Utilization.CustomizedThisWeek')}
          </span>
          <button
            type="button"
            disabled={isLocked}
            onClick={onReset}
            className="text-xs font-medium text-brand-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50">
            {t('Schedule.Detail.Sidebar.Utilization.Reset')}
          </button>
        </div>
      )}
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={capPct}
        disabled={isLocked}
        onChange={(event) => {
          committedPct.current = null;
          setCapPct(Number(event.target.value));
        }}
        onMouseUp={commitCap}
        onTouchEnd={commitCap}
        onBlur={commitCap}
        className="mt-1 w-full accent-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
      />
      <div className="flex items-center justify-between text-[11px] text-gray-400">
        <span>{t('Schedule.Detail.Sidebar.Utilization.Percent', { value: 0 })}</span>
        <span>{t('Schedule.Detail.Sidebar.Utilization.TickSlots', { capSlots, typicalSlots: totalSlots })}</span>
        <span>{t('Schedule.Detail.Sidebar.Utilization.Percent', { value: 100 })}</span>
      </div>

      <div className="mt-3 flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-2"
          style={{ width: totalSlots > 0 ? `${(capSlots / totalSlots) * 100}%` : '0%', backgroundColor: CHART_TEAL }}
        />
        <div
          className="h-2 bg-brand-500"
          style={{ width: totalSlots > 0 ? `${(roomSlots / totalSlots) * 100}%` : '0%' }}
        />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] leading-4 text-gray-500">
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="h-[7px] w-[7px] shrink-0 rounded-[2px]" style={{ backgroundColor: CHART_TEAL }} />
          {t('Schedule.Detail.Sidebar.Utilization.CappedFirstLegend', { count: capSlots })}
        </span>
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="h-[7px] w-[7px] shrink-0 rounded-[2px] bg-brand-500" />
          {t('Schedule.Detail.Sidebar.Utilization.ScheduledLegend', { count: roomSlots })}
        </span>
      </div>
    </div>
  );
};

export default AlertCeilingUtilization;
